import React, { useState, useEffect } from 'react';
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react';
import LayoutAnalyzer from './LayoutAnalyzer';
import WhatsNew, { hasUnreadWhatsNew } from './WhatsNew';
import GatedButton from './GatedButton';
import { LATEST_VERSION } from '../data/changelog';

// ---- Inline SVG icons ----------------------------------------------------
// All icons use currentColor so they inherit the button's text color.
// Size defaults to 14px (matches old emoji/text glyph footprint inside btnS).
// Kept as plain functional components — zero deps, no new files to import.

const IconAI = ({ size = 14 }) => (
  // 4-point sparkle + 2 small accent stars — the "AI Options" sparkle
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3 L13.5 9 L19.5 10.5 L13.5 12 L12 18 L10.5 12 L4.5 10.5 L10.5 9 Z"
      fill="currentColor" />
    <circle cx="18" cy="5" r="1" fill="currentColor" />
    <circle cx="5" cy="18" r="1" fill="currentColor" />
  </svg>
);

const IconHelp = ({ size = 14 }) => (
  // Circle with question mark — color comes from parent (currentColor)
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M9.5 9 C9.5 7.5 10.5 6.5 12 6.5 C13.5 6.5 14.5 7.5 14.5 9 C14.5 10.5 12 10.5 12 13"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    <circle cx="12" cy="17" r="1.1" fill="currentColor" />
  </svg>
);

const IconSave = ({ size = 14 }) => (
  // Classic floppy disk silhouette — universal "save" affordance
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 5 H16 L19 8 V19 H5 Z"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none" />
    <rect x="8" y="5" width="7" height="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="8" y="13" width="8" height="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
  </svg>
);

const IconLoad = ({ size = 14 }) => (
  // Open folder — matches the amber folder from the icon pack but recolors via currentColor
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 7 H9 L11 9 H21 V19 H3 Z"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none" />
  </svg>
);

const IconArrowRight = ({ size = 14 }) => (
  // Right-pointing arrow — used for "forward" navigation
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12 H18 M13 7 L18 12 L13 17"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconArrowLeft = ({ size = 14 }) => (
  // Left-pointing arrow — used for "back" / configure navigation
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M19 12 H6 M11 7 L6 12 L11 17"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconUser = ({ size = 14 }) => (
  // Generic user silhouette — for the Sign In button
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" fill="none" />
    <path d="M5 20 C5 16 8 14 12 14 C16 14 19 16 19 20"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

// Kebab (three vertical dots) — opens the overflow menu
const IconKebab = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </svg>
);

// Check mark — completed steps in the stepper
const IconCheck = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12 L10 17 L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Down chevron — AI Tools dropdown trigger
const IconChevronDown = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 9 L12 15 L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Info circle — used on the About row
const IconInfo = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="8" r="1.2" fill="currentColor" />
    <path d="M12 11 V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// --------------------------------------------------------------------------

export default function TopBar({ step, shop, placed, doors, drains, exportForm, onNav, onSave, onLoad, onSketchImport, onPromptImport, isDirty, hasFile, canLeaveSetup = true }) {
  const [showKebab, setShowKebab] = useState(false);        // top-right overflow menu
  const [showAi, setShowAi] = useState(false);               // AI Tools dropdown
  const [showAnalyzer, setShowAnalyzer] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  const equipment = placed.filter(p => !p.isRoom);

  // Save is now a one-click action — project name lives on the Setup page
  // (synced via exportForm), so no modal is needed. App.js's handleSave
  // reads exportForm.projectName itself and falls back to "Untitled Project".
  const handleSave = () => {
    onSave();
  };

  const handleFileLoad = (e) => {
    const file = e.target.files[0];
    if (file) onLoad(file);
    e.target.value = '';
  };

  // Close About modal on Escape — small UX touch that users expect from modals
  useEffect(() => {
    if (!showAbout) return;
    const onKey = e => { if (e.key === 'Escape') setShowAbout(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAbout]);

  // Closes the kebab menu — used by every action inside it
  const closeKebab = () => setShowKebab(false);

  // ─── Stepper data ──────────────────────────────────────────────
  // The three steps of the flow. One name per step, used everywhere —
  // replaces the old Configure / Open Floor Plan / Export Layout / Back
  // to Editor buttons that each called the same places something different.
  const STEPS = [
    { id: 'setup', label: 'Setup' },
    { id: 'build', label: 'Floor Plan' },
    { id: 'export', label: 'Export' },
  ];
  const currentIdx = STEPS.findIndex(s => s.id === step);   // -1 on the Help page
  // Leaving Setup is blocked while a free-form shape is open or crossed
  const isBlocked = (targetId) => step === 'setup' && targetId !== 'setup' && !canLeaveSetup;
  const nextStep = currentIdx >= 0 && currentIdx < STEPS.length - 1 ? STEPS[currentIdx + 1] : null;

  return (
    <>
      <div style={{
        height: 48, background: '#060d18',
        borderBottom: '1px solid #162840',
        display: 'flex', alignItems: 'center',
        gap: 0, flexShrink: 0, overflow: 'visible',
        position: 'relative', zIndex: 100,
      }}>

        {/* Logo — clickable, opens About modal. Subtitle shows shop size and
            item count once there's a floor plan (replaces the old info block). */}
        <button
          onClick={() => setShowAbout(true)}
          title="About ShopBuilder Pro"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '0 16px',
            height: '100%', flexShrink: 0,
            background: 'transparent',
            borderTop: 'none', borderBottom: 'none', borderLeft: 'none',
            borderRight: '1px solid #162840',
            cursor: 'pointer', fontFamily: 'inherit',
            transition: 'background .15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(51,153,255,.05)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <div style={{ width: 30, height: 30, background: 'linear-gradient(135deg,#003d80,#0066cc)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: '-.5px' }}>SB</div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#e8f4ff', lineHeight: 1.1 }}>
              <span style={{ color: '#3399ff' }}>Shop</span>Builder Pro
            </div>
            <div style={{ fontSize: 11, color: '#8aa8c5' }}>
              {step === 'build' || step === 'export'
                ? `${shop.w}×${shop.h} ft · ${equipment.length} item${equipment.length === 1 ? '' : 's'}`
                : 'CAD Layout Tool'}
            </div>
          </div>
        </button>

        {/* ─── Stepper ─────────────────────────────────────────────
            Centered. Done steps get a green check, the current one is
            filled blue. Every step is clickable (that's the navigation now). */}
        <nav aria-label="Steps" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minWidth: 0 }}>
          {STEPS.map((s, i) => {
            const active = i === currentIdx;
            const done = currentIdx > i;
            const blocked = isBlocked(s.id);
            return (
              <React.Fragment key={s.id}>
                {i > 0 && (
                  <span aria-hidden="true" style={{ width: 28, height: 1, background: currentIdx >= i ? '#3399ff' : '#1e3a54' }} />
                )}
                <button
                  onClick={() => { if (!active && !blocked) onNav(s.id); }}
                  disabled={blocked}
                  aria-current={active ? 'step' : undefined}
                  title={blocked ? 'Close the shop shape (and fix any crossing walls) first' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: active ? 'rgba(51,153,255,.14)' : 'transparent',
                    border: `1px solid ${active ? '#3399ff' : '#1e3a54'}`,
                    borderRadius: 20, padding: '4px 13px 4px 4px',
                    color: active ? '#e8f4ff' : '#a8c4dc',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: active ? 600 : 500,
                    cursor: active ? 'default' : blocked ? 'not-allowed' : 'pointer',
                    opacity: blocked ? 0.45 : 1, whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                    background: active ? '#3399ff' : done ? 'rgba(64,192,96,.18)' : 'transparent',
                    border: active ? 'none' : `1px solid ${done ? '#40c060' : '#3a6a8a'}`,
                    color: active ? '#fff' : done ? '#5fd47c' : '#a8c4dc',
                  }}>
                    {done ? <IconCheck size={12} /> : i + 1}
                  </span>
                  {s.label}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        {/* ─── Right actions ───────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', height: '100%', flexShrink: 0, position: 'relative' }}>

          {/* Save status — "Not saved to file" when the layout differs from the
              last saved/loaded .sbpro. Work is still autosaved in the browser,
              so the tooltip says so rather than sounding alarming. */}
          {step !== 'help' && isDirty && (
            <span
              title="Your work is backed up in this browser. Use Save to keep a file copy you can share or open on another computer."
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#ffb84d', whiteSpace: 'nowrap', cursor: 'help' }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ffb84d' }} />
              Not saved to file
            </span>
          )}
          {step !== 'help' && !isDirty && hasFile && (
            <span style={{ fontSize: 12, color: '#5fd47c', whiteSpace: 'nowrap' }}>✓ Saved</span>
          )}

          {/* AI Tools — its own button now (was ⋮ → AI Options → flyout).
              Only on Setup and Floor Plan, where the imports/analysis make sense. */}
          {(step === 'setup' || step === 'build') && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowAi(v => !v)}
                aria-expanded={showAi}
                style={{ ...btnS, background: 'rgba(170,102,255,.12)', borderColor: '#aa66ff88', color: '#d4b3ff', fontWeight: 600 }}
              >
                <IconAI size={14} />
                AI Tools
                <IconChevronDown size={12} />
              </button>
              {showAi && (
                <>
                  {/* Click-outside overlay */}
                  <div onClick={() => setShowAi(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 999,
                    background: '#0a1628', border: '1px solid #aa66ff44', borderRadius: 10, padding: 6,
                    boxShadow: '0 8px 32px rgba(0,0,0,.8)', minWidth: 250,
                  }}>
                    {/* When signed out, GatedButton opens Clerk's sign-in instead of
                        calling onClick — so the dropdown only closes on a real action */}
                    <GatedButton
                      onClick={() => { setShowAi(false); onPromptImport(); }}
                      style={aiMenuBtn}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(170,102,255,.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <IconAI size={12} />
                      <span style={{ flex: 1 }}>Describe Layout</span>
                      <span style={{ fontSize: 11, color: '#9a7acc' }}>Text → Plan</span>
                    </GatedButton>
                    <GatedButton
                      onClick={() => { setShowAi(false); onSketchImport(); }}
                      style={aiMenuBtn}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(170,102,255,.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <IconAI size={12} />
                      <span style={{ flex: 1 }}>Import Sketch</span>
                      <span style={{ fontSize: 11, color: '#9a7acc' }}>Image → Plan</span>
                    </GatedButton>
                    {step === 'build' && (
                      <GatedButton
                        onClick={() => { setShowAi(false); setShowAnalyzer(true); }}
                        style={aiMenuBtn}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(170,102,255,.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <IconAI size={12} />
                        <span style={{ flex: 1 }}>Analyze Layout</span>
                        <span style={{ fontSize: 11, color: '#9a7acc' }}>Review Plan</span>
                      </GatedButton>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div style={{ width: 1, height: 20, background: '#162840' }} />

          {/* Save — every step except Help */}
          {step !== 'help' && (
            <button
              onClick={handleSave}
              title="Save this project as a .sbpro file"
              style={isDirty ? { ...btnS, color: '#e8f4ff', borderColor: '#3399ff88' } : btnS}
            >
              <IconSave size={14} />
              Save
            </button>
          )}

          {/* Open (load a .sbpro) */}
          {step !== 'help' && (
            <label title="Open a saved .sbpro project" style={{ ...btnS, cursor: 'pointer' }}>
              <IconLoad size={14} />
              Open
              <input type="file" accept=".sbpro,.json" onChange={handleFileLoad} style={{ display: 'none' }} />
            </label>
          )}

          {/* Primary forward action. Help gets a Back instead. */}
          {nextStep && (
            <button
              onClick={() => { if (!isBlocked(nextStep.id)) onNav(nextStep.id); }}
              disabled={isBlocked(nextStep.id)}
              title={isBlocked(nextStep.id) ? 'Close the shop shape (and fix any crossing walls) first' : undefined}
              style={{
                ...btnS, background: '#0066cc', borderColor: '#3399ff', color: '#fff', fontWeight: 600,
                opacity: isBlocked(nextStep.id) ? 0.45 : 1,
                cursor: isBlocked(nextStep.id) ? 'not-allowed' : 'pointer',
              }}
            >
              Next: {nextStep.label}
              <IconArrowRight size={14} />
            </button>
          )}
          {step === 'help' && (
            // 'back' = return to whichever step Help was opened from (App.js tracks it)
            <button onClick={() => onNav('back')} style={btnS}>
              <IconArrowLeft size={14} />
              Back
            </button>
          )}

          {/* ----- KEBAB OVERFLOW MENU ------------------------------------
              What's New, Help, About. (AI moved to its own button.) */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowKebab(k => !k)}
              title="More options"
              aria-label="More options"
              style={{ ...btnS, padding: '6px 10px', position: 'relative' }}
            >
              <IconKebab size={16} />
              {/* Unread What's New dot — on the ⋮ itself so users see it
                  without opening the menu. Clears after What's New is opened. */}
              {hasUnreadWhatsNew() && (
                <span style={{
                  position: 'absolute', top: 3, right: 3,
                  width: 7, height: 7, borderRadius: '50%',
                  background: '#ff4444', boxShadow: '0 0 6px rgba(255,68,68,0.6)',
                }} />
              )}
            </button>

            {showKebab && (
              <>
                <div onClick={closeKebab} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
                <div style={{
                  position: 'fixed', top: 52, right: 12,
                  background: '#0a1628', border: '1px solid #1e3a54',
                  borderRadius: 10, padding: 6, zIndex: 999,
                  boxShadow: '0 8px 32px rgba(0,0,0,.8)', minWidth: 230,
                }}>
                  {/* IMPORTANT: do NOT wrap WhatsNew with onClick={closeKebab}.
                      Its modal state lives inside WhatsNew; closing the kebab
                      would unmount it before the modal renders. The modal's
                      z-index (1000) covers the kebab (999) anyway. */}
                  <WhatsNew renderAs="menuRow" />

                  <button
                    onClick={() => { closeKebab(); onNav('help'); }}
                    style={kebabRow}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(51,153,255,.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <IconHelp size={14} />
                    <span style={{ flex: 1, textAlign: 'left' }}>Help &amp; Docs</span>
                  </button>

                  <div style={{ height: 1, background: '#162840', margin: '4px 6px' }} />

                  <button
                    onClick={() => { closeKebab(); setShowAbout(true); }}
                    style={kebabRow}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(51,153,255,.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <IconInfo size={14} />
                    <span style={{ flex: 1, textAlign: 'left' }}>About ShopBuilder Pro</span>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Auth cluster — small divider then sign-in button OR user avatar */}
          <div style={{ width: 1, height: 20, background: '#162840' }} />
          <SignedOut>
            {/* mode="modal" keeps user in the app rather than redirecting away */}
            <SignInButton mode="modal">
              <button style={{ ...btnS, color: '#66b3ff', borderColor: '#3399ff66' }}>
                <IconUser size={14} />
                Sign In
              </button>
            </SignInButton>
          </SignedOut>
          <SignedIn>
            {/* Clerk's prebuilt avatar/dropdown — handles sign-out, profile, etc. */}
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </div>
      </div>

      {/* AI Layout Analyzer Modal */}
      {showAnalyzer && (
        <LayoutAnalyzer
          shop={shop}
          placed={placed}
          doors={doors || []}
          drains={drains || []}
          form={exportForm || {}}
          onClose={() => setShowAnalyzer(false)}
        />
      )}

      {/* About modal — opened by clicking the SB logo on the left, or About row in kebab */}
      {showAbout && (
        <div
          onClick={() => setShowAbout(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#0a1628', border: '1px solid #162840', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 8px 40px rgba(0,0,0,.6)', position: 'relative' }}
          >
            {/* Close X — top right */}
            <button
              onClick={() => setShowAbout(false)}
              aria-label="Close"
              style={{ position: 'absolute', top: 10, right: 12, background: 'transparent', border: 'none', color: '#4a6a8a', fontSize: 20, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1, padding: 4 }}
            >
              ×
            </button>

            {/* Logo block, scaled up vs the topbar version */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <div style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#003d80,#0066cc)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-.5px' }}>SB</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#e8f4ff', lineHeight: 1.1 }}>
                  <span style={{ color: '#3399ff' }}>Shop</span>Builder Pro
                </div>
                <div style={{ fontSize: 11, color: '#8aa8c5', letterSpacing: '.04em', marginTop: 3 }}>
                  Version {LATEST_VERSION} · CAD Layout Tool
                </div>
              </div>
            </div>

            {/* Tagline */}
            <div style={{ fontSize: 12, color: '#8aa8c5', lineHeight: 1.5, marginBottom: 18 }}>
              Professional shop layout planning for automotive service equipment. Drag, drop, measure, and export ready-to-share floor plans.
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#162840', margin: '6px 0 16px' }} />

            {/* Credit + contact */}
            <div style={{ fontSize: 11, color: '#6a9fc0', lineHeight: 1.6 }}>
              <div>Created by <span style={{ color: '#c8dff0', fontWeight: 600 }}>Chris Logan</span></div>
              <div style={{ marginTop: 4 }}>
                <a
                  href="mailto:chris@it3dp.ca"
                  style={{ color: '#3399ff', textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                >
                  © Copyright 2026 All Rights Reserved - chris@it3dp.ca
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

const btnS = {
  background: 'transparent', border: '1px solid #1e3a54',
  borderRadius: 7, color: '#a8c4dc', fontSize: 12,
  fontWeight: 500, padding: '6px 12px',
  cursor: 'pointer', fontFamily: 'inherit',
  whiteSpace: 'nowrap', display: 'flex',
  alignItems: 'center', gap: 6,
};

const aiMenuBtn = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', background: 'transparent',
  border: 'none', borderRadius: 7, color: '#d4b3ff',
  fontSize: 13, fontWeight: 600, padding: '9px 14px',
  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
  transition: 'background .12s',
};

// Shared row style for items inside the kebab menu — bell, help, about, etc.
// position: relative needed so the absolutely-positioned WhatsNew overlay anchors here.
const kebabRow = {
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', background: 'transparent',
  border: 'none', borderRadius: 6, color: '#c8dff0',
  fontSize: 13, fontWeight: 500, padding: '9px 12px',
  cursor: 'pointer', fontFamily: 'inherit',
  transition: 'background .12s',
  position: 'relative',
};
